import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Alert, AlertDescription } from '../ui/alert';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Label } from '../ui/label';
import { storeManagementService, IShopifyStore } from '../../services/StoreManagementService';

interface StoreManagementDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  store?: IShopifyStore;
  onSuccess?: () => void;
}

const StoreManagementDialog: React.FC<StoreManagementDialogProps> = ({
  isOpen,
  onOpenChange,
  store,
  onSuccess
}) => {
  const [storeName, setStoreName] = useState('');
  const [storeUrl, setStoreUrl] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && store) {
      setStoreName(store.storeName);
      setStoreUrl(store.storeUrl);
      setAccessToken(store.accessToken);
    } else if (!isOpen) {
      setStoreName('');
      setStoreUrl('');
      setAccessToken('');
      setError('');
    }
  }, [isOpen, store]);

  const handleSubmit = async () => {
    try {
      setError('');
      setIsLoading(true);

      // Validate inputs
      if (!storeName || !storeUrl || !accessToken) {
        throw new Error('All fields are required');
      }

      // Format store URL (remove https:// if present)
      const formattedStoreUrl = storeUrl.replace(/^https?:\/\//, '');

      if (store?.id) {
        // Update existing store
        await storeManagementService.updateStore(store.id, {
          storeName,
          storeUrl: formattedStoreUrl,
          accessToken
        });
      } else {
        // Add new store
        await storeManagementService.addStore({
          storeName,
          storeUrl: formattedStoreUrl,
          accessToken
        });
      }

      onSuccess?.();
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'An error occurred while saving the store');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>{store ? 'Edit Store' : 'Add New Store'}</DialogTitle>
          <DialogDescription>
            {store
              ? 'Update your Shopify store configuration.'
              : 'Connect a new Shopify store to the system.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="storeName">Store Name</Label>
            <Input
              id="storeName"
              value={storeName}
              onChange={(e) => setStoreName(e.target.value)}
              placeholder="My Shopify Store"
              disabled={isLoading}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="storeUrl">Store URL</Label>
            <Input
              id="storeUrl"
              value={storeUrl}
              onChange={(e) => setStoreUrl(e.target.value)}
              placeholder="mystore.myshopify.com"
              disabled={isLoading}
            />
            <p className="text-sm text-gray-500">
              Enter your *.myshopify.com domain
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="accessToken">Admin API Access Token</Label>
            <Input
              id="accessToken"
              type="password"
              value={accessToken}
              onChange={(e) => setAccessToken(e.target.value)}
              placeholder="shpat_xxxxxxxxxxxxxxxxxxxxx"
              disabled={isLoading}
            />
            <p className="text-sm text-gray-500">
              Generate this token in your Shopify admin under Apps {'>'} App and Sales Channel settings
            </p>
          </div>
        </div>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-4">
          <DialogClose asChild>
            <Button variant="outline" disabled={isLoading}>
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {store ? 'Update Store' : 'Add Store'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StoreManagementDialog;